import React, { useEffect, useMemo, useState } from 'react';
import { Text, Platform, View, TouchableOpacity, Modal, StyleSheet } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';

import { getTipsReport, setTipPaid } from '../../../api/Reports';
import { getReservationHistoryData } from '../../../api/Reservation';
import { getBrandsData } from '../../../api/Price';
import { BasicLayout, CommonContainer } from '../../../common/components/CustomLayout';
import {
  BOHTBody, BOHTD, BOHTDCheckbox, BOHTDPressable, BOHTH2, BOHTHead, BOHTR, BOHTable,
} from '../../../common/components/bohtable';
import { BasicModalContainer, ModalHeader, ModalBody } from '../../../common/components/basicmodal';
import { msgStr } from '../../../common/constants/Message';
import { useAlertModal } from '../../../common/hooks';
import { BOHToolbar, BOHTlbrSearchPicker, renderBOHTlbDatePicker } from '../../../common/components/bohtoolbar';
import { TextdefaultSize, TextMediumSize } from '../../../common/constants/Fonts';
import { formatDate, formatDateTime, getStartEndDates } from '../../../common/utils/DateUtils';

const UNASSIGNED = 'Unassigned';

const fmtCurrency = (val) =>
  (Number(val) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const staffName = (row) => (row?.driverDeliveredBy && String(row.driverDeliveredBy).trim()) || UNASSIGNED;

const TipsPaidReport = ({ navigation }) => {
  const { showAlert } = useAlertModal();

  const [rawData, setRawData] = useState<any[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(false);

  const [brands, setBrands] = useState<any[]>([]);

  const [viewMode, setViewMode] = useState<'reservation' | 'staff'>('reservation');
  const [staffFilter, setStaffFilter] = useState<string>('');
  const [paidFilter, setPaidFilter] = useState<'all' | 'paid' | 'unpaid'>('all');

  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyRows, setHistoryRows] = useState<any[]>([]);
  const [historyTitle, setHistoryTitle] = useState('');

  const today = new Date();
  const [searchOptions, setSearchOptions] = useState({
    start_date: formatDate(getStartEndDates(new Date(today)).start),
    end_date: formatDate(getStartEndDates(new Date(today)).end),
    brand: '',
  });

  const changeSearchOptions = (key, val) => {
    setSearchOptions(prev => ({ ...prev, [key]: val }));
  };

  useEffect(() => {
    getBrandsData((jsonRes, status) => {
      if (status === 200 && Array.isArray(jsonRes)) setBrands(jsonRes);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    setUpdateTrigger(true);
  }, [searchOptions]);

  useEffect(() => {
    if (updateTrigger) getTable();
  }, [updateTrigger]);

  const getTable = () => {
    setLoading(true);
    getTipsReport({ searchOptions }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setRawData(Array.isArray(jsonRes) ? jsonRes : []);
          setUpdateTrigger(false);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
      setLoading(false);
    });
  };

  // Distinct staff names for the filter dropdown.
  const staffOptions = useMemo(() => {
    const set = new Set<string>();
    rawData.forEach(r => set.add(staffName(r)));
    return Array.from(set).sort();
  }, [rawData]);

  // Rows after the paid/unpaid filter (drives both the By Staff rollup and,
  // once the staff filter is applied, the By Reservation view).
  const paidFilteredRows = useMemo(() => {
    return rawData.filter(r => {
      if (paidFilter === 'paid') return !!r.driver_tip_paid_at;
      if (paidFilter === 'unpaid') return !r.driver_tip_paid_at;
      return true;
    });
  }, [rawData, paidFilter]);

  // By Reservation view additionally respects the staff dropdown.
  const reservationRows = useMemo(() => {
    if (!staffFilter) return paidFilteredRows;
    return paidFilteredRows.filter(r => staffName(r) === staffFilter);
  }, [paidFilteredRows, staffFilter]);

  // By Staff aggregates (always the full rollup for the paid filter / date range).
  const staffAggregates = useMemo(() => {
    const map = new Map<string, any>();
    paidFilteredRows.forEach(r => {
      const name = staffName(r);
      const tip = Number(r.driver_tip) || 0;
      const paid = !!r.driver_tip_paid_at;
      if (!map.has(name)) map.set(name, { staff: name, count: 0, total: 0, paid: 0, remaining: 0 });
      const agg = map.get(name);
      agg.count += 1;
      agg.total += tip;
      if (paid) agg.paid += tip; else agg.remaining += tip;
    });
    const rows = Array.from(map.values()).sort((a, b) => b.remaining - a.remaining);
    const grand = rows.reduce(
      (acc, r) => {
        acc.count += r.count; acc.total += r.total; acc.paid += r.paid; acc.remaining += r.remaining;
        return acc;
      },
      { staff: 'Total', count: 0, total: 0, paid: 0, remaining: 0 }
    );
    return { rows, grand };
  }, [paidFilteredRows]);

  const togglePaid = (row) => {
    const nextPaid = !row.driver_tip_paid_at;
    setTipPaid({ id: row.id, paid: nextPaid }, (jsonRes, status) => {
      if (status === 200) {
        setRawData(prev => prev.map(r =>
          r.id === row.id ? { ...r, driver_tip_paid_at: jsonRes?.driver_tip_paid_at ?? (nextPaid ? new Date().toISOString() : null) } : r
        ));
      } else if (status === 500) {
        showAlert('error', msgStr('serverError'));
      } else {
        showAlert('error', (jsonRes && jsonRes.error) || msgStr('unknownError'));
      }
    });
  };

  const openHistory = (row) => {
    setHistoryTitle(`Order #${row.order_number} — Tip History`);
    setHistoryRows([]);
    setHistoryVisible(true);
    getReservationHistoryData({ order_number: row.order_number }, (jsonRes, status) => {
      if (status === 200 && Array.isArray(jsonRes)) {
        const tipEvents = jsonRes
          .filter((v: any) => {
            const inChanges = v.changes && Object.prototype.hasOwnProperty.call(v.changes, 'driver_tip_paid_at');
            const inRevChanges = Array.isArray(v.revisionChanges) &&
              v.revisionChanges.some((c: any) => c.path === 'driver_tip_paid_at');
            return inChanges || inRevChanges;
          })
          .map((v: any) => ({
            userName: v.userName || 'Unknown',
            timestamp: v.timestamp,
            paid: v.changes ? !!v.changes.driver_tip_paid_at : false,
          }));
        setHistoryRows(tipEvents);
      } else {
        showAlert('error', msgStr('unknownError'));
      }
    });
  };

  const drillIntoStaff = (name) => {
    setStaffFilter(name);
    setViewMode('reservation');
  };

  // ── Renders ──────────────────────────────────────────────────────────────
  const RESV_WIDTHS = [180, 120, 120, 100, 160, 70, 160, 80];
  const STAFF_WIDTHS = [200, 90, 140, 140, 160];

  const renderReservationTable = () => (
    <BOHTable style={{ flex: 'none' } as any}>
      <BOHTHead>
        <BOHTR>
          <BOHTH2 width={RESV_WIDTHS[0]}>{'Customer'}</BOHTH2>
          <BOHTH2 width={RESV_WIDTHS[1]}>{'Order #'}</BOHTH2>
          <BOHTH2 width={RESV_WIDTHS[2]}>{'Total'}</BOHTH2>
          <BOHTH2 width={RESV_WIDTHS[3]}>{'Tip'}</BOHTH2>
          <BOHTH2 width={RESV_WIDTHS[4]}>{'Delivered By'}</BOHTH2>
          <BOHTH2 width={RESV_WIDTHS[5]}>{'Paid'}</BOHTH2>
          <BOHTH2 width={RESV_WIDTHS[6]}>{'Paid At'}</BOHTH2>
          <BOHTH2 width={RESV_WIDTHS[7]}>{'History'}</BOHTH2>
        </BOHTR>
      </BOHTHead>
      <BOHTBody>
        {reservationRows.map((row, index) => (
          <BOHTR key={row.id ?? index}>
            <BOHTD width={RESV_WIDTHS[0]}>{(row.customer_name || '').trim() || '-'}</BOHTD>
            <BOHTD width={RESV_WIDTHS[1]}>{row.order_number}</BOHTD>
            <BOHTD width={RESV_WIDTHS[2]} textAlign={'right'}>{fmtCurrency(row.total_price)}</BOHTD>
            <BOHTD width={RESV_WIDTHS[3]} textAlign={'right'}>{fmtCurrency(row.driver_tip)}</BOHTD>
            <BOHTD width={RESV_WIDTHS[4]}>{staffName(row)}</BOHTD>
            <BOHTDCheckbox
              width={RESV_WIDTHS[5]}
              value={!!row.driver_tip_paid_at}
              onValueChange={() => togglePaid(row)}
            />
            <BOHTD width={RESV_WIDTHS[6]}>
              {row.driver_tip_paid_at ? formatDateTime(new Date(row.driver_tip_paid_at)) : '-'}
            </BOHTD>
            <BOHTDPressable width={RESV_WIDTHS[7]} textAlign={'center'} onPress={() => openHistory(row)}>
              <FontAwesome5 size={TextMediumSize} name="history" color="#0d6efd" />
            </BOHTDPressable>
          </BOHTR>
        ))}
      </BOHTBody>
    </BOHTable>
  );

  const renderStaffTable = () => (
    <BOHTable style={{ flex: 'none' } as any}>
      <BOHTHead>
        <BOHTR>
          <BOHTH2 width={STAFF_WIDTHS[0]}>{'Staff'}</BOHTH2>
          <BOHTH2 width={STAFF_WIDTHS[1]}>{'# Tips'}</BOHTH2>
          <BOHTH2 width={STAFF_WIDTHS[2]}>{'Total Tips'}</BOHTH2>
          <BOHTH2 width={STAFF_WIDTHS[3]}>{'Paid'}</BOHTH2>
          <BOHTH2 width={STAFF_WIDTHS[4]}>{'Remaining'}</BOHTH2>
        </BOHTR>
      </BOHTHead>
      <BOHTBody>
        {staffAggregates.rows.map((agg, index) => (
          <BOHTR key={index}>
            <BOHTDPressable width={STAFF_WIDTHS[0]} onPress={() => drillIntoStaff(agg.staff)}>
              <Text style={{ color: '#0d6efd' }}>{agg.staff}</Text>
            </BOHTDPressable>
            <BOHTD width={STAFF_WIDTHS[1]} textAlign={'right'}>{agg.count}</BOHTD>
            <BOHTD width={STAFF_WIDTHS[2]} textAlign={'right'}>{fmtCurrency(agg.total)}</BOHTD>
            <BOHTD width={STAFF_WIDTHS[3]} textAlign={'right'}>{fmtCurrency(agg.paid)}</BOHTD>
            <BOHTD width={STAFF_WIDTHS[4]} textAlign={'right'}>{fmtCurrency(agg.remaining)}</BOHTD>
          </BOHTR>
        ))}
        {staffAggregates.rows.length > 0 && (
          <BOHTR>
            <BOHTD width={STAFF_WIDTHS[0]}><Text style={{ fontWeight: '700' }}>{'Total'}</Text></BOHTD>
            <BOHTD width={STAFF_WIDTHS[1]} textAlign={'right'}>{staffAggregates.grand.count}</BOHTD>
            <BOHTD width={STAFF_WIDTHS[2]} textAlign={'right'}>{fmtCurrency(staffAggregates.grand.total)}</BOHTD>
            <BOHTD width={STAFF_WIDTHS[3]} textAlign={'right'}>{fmtCurrency(staffAggregates.grand.paid)}</BOHTD>
            <BOHTD width={STAFF_WIDTHS[4]} textAlign={'right'}>{fmtCurrency(staffAggregates.grand.remaining)}</BOHTD>
          </BOHTR>
        )}
      </BOHTBody>
    </BOHTable>
  );

  const tableElement = useMemo(
    () => (viewMode === 'staff' ? renderStaffTable() : renderReservationTable()),
    [viewMode, reservationRows, staffAggregates]
  );

  const setDate = (key) => (date) => {
    const formatted = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    changeSearchOptions(key, formatted);
  };

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => navigation.navigate('Reports')}
      screenName={'Tips Paid Report'}
      isLoading={isLoading}
    >
      <CommonContainer>
        <BOHToolbar style={{ zIndex: 10, flexWrap: 'wrap' }}>
          <Text style={{ marginRight: 8, fontSize: TextdefaultSize }}>Start</Text>
          {Platform.OS === 'web' && renderBOHTlbDatePicker(searchOptions.start_date, setDate('start_date'))}
          <Text style={{ marginHorizontal: 8, fontSize: TextdefaultSize }}>End</Text>
          {Platform.OS === 'web' && renderBOHTlbDatePicker(searchOptions.end_date, setDate('end_date'))}
          <BOHTlbrSearchPicker
            width={140}
            boxStyle={{ margin: 0 }}
            label="Brand"
            items={[{ label: 'All', value: '' }, ...brands.map((b: any) => ({ label: b.brand, value: b.id }))]}
            selectedValue={searchOptions.brand}
            onValueChange={(val) => changeSearchOptions('brand', val)}
          />
        </BOHToolbar>

        <BOHToolbar style={{ zIndex: 9, flexWrap: 'wrap' }}>
          <BOHTlbrSearchPicker
            width={160}
            boxStyle={{ margin: 0 }}
            label="Staff"
            items={[{ label: 'All', value: '' }, ...staffOptions.map((s) => ({ label: s, value: s }))]}
            selectedValue={staffFilter}
            onValueChange={(val) => setStaffFilter(String(val))}
          />
          <BOHTlbrSearchPicker
            width={130}
            boxStyle={{ margin: 0 }}
            label="Status"
            items={[
              { label: 'All', value: 'all' },
              { label: 'Paid', value: 'paid' },
              { label: 'Unpaid', value: 'unpaid' },
            ]}
            selectedValue={paidFilter}
            onValueChange={(val) => setPaidFilter(val as any)}
          />
          <View style={styles.toggleGroup}>
            <TouchableOpacity
              style={[styles.toggleBtn, viewMode === 'reservation' && styles.toggleBtnActive]}
              onPress={() => setViewMode('reservation')}
            >
              <Text style={viewMode === 'reservation' ? styles.toggleTextActive : styles.toggleText}>By Reservation</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.toggleBtn, viewMode === 'staff' && styles.toggleBtnActive]}
              onPress={() => setViewMode('staff')}
            >
              <Text style={viewMode === 'staff' ? styles.toggleTextActive : styles.toggleText}>By Staff</Text>
            </TouchableOpacity>
          </View>
        </BOHToolbar>

        {tableElement}
      </CommonContainer>

      <Modal animationType="none" transparent visible={historyVisible}>
        <BasicModalContainer>
          <ModalHeader label={historyTitle} closeModal={() => setHistoryVisible(false)} />
          <ModalBody>
            {historyRows.length === 0 ? (
              <Text style={{ padding: 12 }}>No paid/unpaid changes recorded yet.</Text>
            ) : (
              <BOHTable>
                <BOHTHead>
                  <BOHTR>
                    <BOHTH2 width={140}>{'Action'}</BOHTH2>
                    <BOHTH2 width={180}>{'By'}</BOHTH2>
                    <BOHTH2 width={200}>{'When'}</BOHTH2>
                  </BOHTR>
                </BOHTHead>
                <BOHTBody>
                  {historyRows.map((h, index) => (
                    <BOHTR key={index}>
                      <BOHTD width={140}>{h.paid ? 'Marked PAID' : 'Marked UNPAID'}</BOHTD>
                      <BOHTD width={180}>{h.userName}</BOHTD>
                      <BOHTD width={200}>{h.timestamp ? formatDateTime(new Date(h.timestamp)) : '-'}</BOHTD>
                    </BOHTR>
                  ))}
                </BOHTBody>
              </BOHTable>
            )}
          </ModalBody>
        </BasicModalContainer>
      </Modal>
    </BasicLayout>
  );
};

const styles = StyleSheet.create({
  toggleGroup: {
    flexDirection: 'row',
    marginLeft: 25,
    borderWidth: 1,
    borderColor: '#0d6efd',
    borderRadius: 4,
    overflow: 'hidden',
  },
  toggleBtn: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: 'white',
  },
  toggleBtnActive: {
    backgroundColor: '#0d6efd',
  },
  toggleText: {
    color: '#0d6efd',
    fontSize: TextdefaultSize,
  },
  toggleTextActive: {
    color: 'white',
    fontSize: TextdefaultSize,
  },
});

export default TipsPaidReport;
